import React, { RefObject, useCallback, useState } from 'react';

import WideContainer from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { TSubmitFunc } from '~/src/components/RequestFormPhone';
import { NON_BREAKING_SPACE } from '~/src/constants/simbols';
import { useIntersectionObserver } from '~/src/helpers/hooks/useIntersectionObserver';

import * as ST from './styled';

export enum EMediaWidth {
    desktop = '(min-width: 1000px)',
    tablet = '(max-width: 999px)',
    mobile = '(max-width: 767px)',
}

export interface IStatisticsProps {
    submitRequest: TSubmitFunc,
    onStartFormFilling?: () => void,
    formRef: RefObject<HTMLDivElement>,
}

export const Statistics = ({ submitRequest, onStartFormFilling, formRef }: IStatisticsProps): JSX.Element => {
    const isDesktop = useMatchMedia(EMediaWidth.desktop);
    const isTablet = useMatchMedia(EMediaWidth.tablet);
    const isMobile = useMatchMedia(EMediaWidth.mobile);

    const [isVisible, setIsVisible] = useState(false);
    const [isVisibleWrap, setIsVisibleWrap] = useState(false);

    const observeHandler = useCallback((entries) => {
        const [firstElem] = entries || [];
        setIsVisible(firstElem?.isIntersecting);
    }, []);

    const observeWrapHandler = useCallback((entries) => {
        const [firstElem] = entries || [];
        setIsVisibleWrap(firstElem?.isIntersecting);
    }, []);

    const blockRef = useIntersectionObserver(observeHandler, { root: null, threshold: 0.4 });
    const blockWrapRef = useIntersectionObserver(observeWrapHandler, { root: null, threshold: 0.1 });

    return (
        <ST.Statistics ref={blockRef}>
            <ST.StatElementsWrap ref={blockWrapRef}>
                <WideContainer>
                    <ST.InnerStatElementsWrap>
                        <ST.StatElem>
                            <ST.ElemTitle>Более 10 000 000</ST.ElemTitle>
                            <ST.ElemText>раз в день наши домофоны открывают двери.</ST.ElemText>
                        </ST.StatElem>
                        <ST.StatElem>
                            <ST.ElemTitle>Более 12 000 000</ST.ElemTitle>
                            <ST.ElemText>жителей уже оценили<br />преимущества наших домофонов.</ST.ElemText>
                        </ST.StatElem>
                    </ST.InnerStatElementsWrap>
                </WideContainer>

                {isDesktop && !isTablet &&
                    <>
                        <ST.AnimImageLeft isVisible={isVisible}/>
                        <ST.AnimImageRight isVisible={isVisible}/>
                    </>
                }

                {isTablet && !isMobile &&
                    <>
                        <ST.TabletLeftTop isVisible={isVisible}/>
                        <ST.TabletRightTop isVisible={isVisible}/>
                        <ST.TabletLeftBottom isVisible={isVisible}/>
                        <ST.TabletRightBottom isVisible={isVisible}/>
                    </>
                }

                {isMobile &&
                    <>
                        <ST.MobileLeftTop isVisible={isVisibleWrap}/>
                        <ST.MobileRightTop isVisible={isVisibleWrap}/>
                        <ST.MobileLeftBottom isVisible={isVisibleWrap}/>
                        <ST.MobileRightBottom isVisible={isVisibleWrap}/>
                    </>
                }
            </ST.StatElementsWrap>

            <WideContainer>
                <ST.FormWrap ref={formRef}>
                    <ST.Description>
                        <ST.FormTitle>{`Оставьте заявку на${NON_BREAKING_SPACE}подключение`}</ST.FormTitle>
                        <ST.FormText>Перезвоним и ответим на ваши вопросы </ST.FormText>

                        <ST.SRequestFormPhone
                            templateVariant={'second'}
                            isLoading={false}
                            isMobile={isTablet}
                            submitRequest={submitRequest}
                            onStartFormFilling={onStartFormFilling}
                            isCheckInvertColor={true}
                            btnText='Перезвоните мне'
                            data-test='request-form'
                        />
                    </ST.Description>
                    <ST.ImgWrap>
                        <ST.SPicture>
                            <source srcSet={require('~/static/images/domofon/statistics/phone.webp')} type='image/webp'/>
                            <ST.Img
                                src={require('~/static/images/domofon/statistics/phone.png')}
                                alt='домофон'
                            />
                        </ST.SPicture>
                    </ST.ImgWrap>
                </ST.FormWrap>
            </WideContainer>
        </ST.Statistics>
    );
};
